import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Clock, Wrench, Landmark, UserCheck, Loader2 } from 'lucide-react'
import { dealsApi, type ReviewQueueDeal } from '@api/client'
import { formatCurrency, formatDate } from '@utils/format'

// The moment a deal entered its current review step: the impl-divert time for awaiting
// deals, else the standard-submit time, else (legacy rows) last-touched. Drives both the
// "Submitted" date and the "Waiting" age so neither is skewed by later edits.
function submittedAt(d: ReviewQueueDeal): string | null | undefined {
  return d.impl_review_requested_at || d.review_submitted_at || d.updated_at
}

// ---- helpers ---------------------------------------------------------------

type Holding = {
  label: string
  who: string
  icon: typeof Wrench
  cls: string // tailwind classes for the badge
}

// Who currently owns the next action, derived from the deal's review state.
function getHolding(d: ReviewQueueDeal): Holding {
  if (d.impl_review_status === 'awaiting') {
    return { label: 'Implementations', who: 'Alitia · Aju', icon: Wrench, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' }
  }
  if (d.impl_review_status === 'reviewed') {
    return { label: 'Awaiting AE', who: d.review_submitted_by_name || '—', icon: UserCheck, cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30' }
  }
  // status === 'in_review'
  return { label: 'Finance', who: 'Jacqui · Rob', icon: Landmark, cls: 'bg-violet-500/15 text-violet-400 border-violet-500/30' }
}

// Deal-assessment RAG dot — mirrors the deal-assessment score.
function assessmentDot(assessment: string | null | undefined): { cls: string; label: string } {
  switch ((assessment || '').toLowerCase()) {
    case 'great': return { cls: 'bg-emerald-500', label: 'Great' }
    case 'good': return { cls: 'bg-green-500', label: 'Good' }
    case 'average': return { cls: 'bg-amber-500', label: 'Average' }
    case 'poor': case 'bad': return { cls: 'bg-red-500', label: 'Poor' }
    default: return { cls: 'bg-gray-500', label: 'No score' }
  }
}

function marginColour(margin: number | null | undefined): string {
  if (margin == null) return 'text-[var(--text-muted)]'
  if (margin >= 70) return 'text-emerald-400'
  if (margin >= 65) return 'text-green-400'
  if (margin >= 60) return 'text-amber-400'
  return 'text-red-400'
}

// Hours since an ISO timestamp, and a compact "2d 4h" label.
function waitingSince(iso: string | null | undefined): { hours: number; label: string } {
  if (!iso) return { hours: 0, label: '—' }
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return { hours: 0, label: '—' }
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000))
  const h = Math.floor(mins / 60)
  if (h < 1) return { hours: 0, label: `${mins}m` }
  if (h < 24) return { hours: h, label: `${h}h` }
  const d = Math.floor(h / 24)
  return { hours: h, label: `${d}d ${h % 24}h` }
}

const SLA_HOURS = 48 // ~2 business days before a review is flagged as aging

// Secondary "review health" signal Paulo asked for: amber when the impl fee
// isn't scope-synced, or the review has been waiting beyond the SLA.
function reviewHealth(d: ReviewQueueDeal, waitingHours: number): { warn: boolean; reasons: string[] } {
  const reasons: string[] = []
  const fee = d.quote?.implementation_fee ?? 0
  if (fee > 0 && !d.quote?.implementation_fee_from_scope) {
    reasons.push('Impl fee not sourced from scope')
  }
  if (waitingHours >= SLA_HOURS) {
    reasons.push(`Waiting > ${Math.round(SLA_HOURS / 24)} days`)
  }
  return { warn: reasons.length > 0, reasons }
}

// ---- component -------------------------------------------------------------

export function ReviewQueueTab() {
  const [deals, setDeals] = useState<ReviewQueueDeal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true)
      const res = await dealsApi.listReviewQueue()
      if (!alive) return
      if (res.success && res.data) {
        setDeals(res.data.items)
        setError(null)
      } else {
        setError(res.error || 'Failed to load review queue')
      }
      setLoading(false)
    })()
    return () => { alive = false }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading review queue…
      </div>
    )
  }

  if (error) {
    return <div className="py-8 text-center text-red-400 text-sm">{error}</div>
  }

  if (deals.length === 0) {
    return (
      <div className="py-12 text-center text-[var(--text-muted)]">
        <CheckCircle2 className="mx-auto mb-2 text-emerald-500" size={28} />
        <p className="text-sm">Nothing in review right now.</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto border border-[var(--border-color)] rounded-lg">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[var(--bg-tertiary)] text-[var(--text-muted)] text-[11px] uppercase tracking-wide">
            <th className="text-left font-medium px-3 py-2">Customer</th>
            <th className="text-left font-medium px-3 py-2">Holding</th>
            <th className="text-center font-medium px-3 py-2">Assessment</th>
            <th className="text-right font-medium px-3 py-2">Waiting</th>
            <th className="text-left font-medium px-3 py-2">Submitted</th>
            <th className="text-right font-medium px-3 py-2">TCV / ARR</th>
            <th className="text-right font-medium px-3 py-2">Margin</th>
            <th className="text-right font-medium px-3 py-2">Impl fee</th>
            <th className="text-left font-medium px-3 py-2">Submitted by</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => {
            const holding = getHolding(d)
            const HoldIcon = holding.icon
            const dot = assessmentDot(d.quote?.overall_assessment)
            const submitted = submittedAt(d)
            const wait = waitingSince(submitted)
            const health = reviewHealth(d, wait.hours)
            const feeSynced = !!d.quote?.implementation_fee_from_scope
            const submittedBy = d.review_submitted_by_name || d.impl_review_requested_by_name || '—'
            return (
              <tr key={d.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]/50">
                {/* Customer */}
                <td className="px-3 py-2">
                  <Link to={`/quote?session=${d.id}`} className="text-[var(--text-primary)] font-medium hover:text-primary">
                    {d.customer_name || 'Untitled'}
                  </Link>
                  {d.hubspot_deal_name && (
                    <div className="text-[11px] text-[var(--text-muted)] truncate max-w-[200px]">{d.hubspot_deal_name}</div>
                  )}
                </td>

                {/* Holding team */}
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${holding.cls}`}>
                    <HoldIcon size={12} /> {holding.label}
                  </span>
                  <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{holding.who}</div>
                </td>

                {/* Assessment RAG + review-health flag */}
                <td className="px-3 py-2">
                  <div className="flex items-center justify-center gap-1.5" title={dot.label}>
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot.cls}`} />
                    {health.warn && (
                      <span title={health.reasons.join(' · ')} className="inline-flex">
                        <AlertTriangle size={13} className="text-amber-400" />
                      </span>
                    )}
                  </div>
                </td>

                {/* Waiting */}
                <td className="px-3 py-2 text-right">
                  <span className={`inline-flex items-center gap-1 ${wait.hours >= SLA_HOURS ? 'text-amber-400' : 'text-[var(--text-secondary)]'}`}>
                    <Clock size={11} /> {wait.label}
                  </span>
                </td>

                {/* Submitted date */}
                <td className="px-3 py-2 text-[var(--text-secondary)] text-[12px] whitespace-nowrap">
                  {submitted ? formatDate(submitted) : '—'}
                </td>

                {/* TCV / ARR */}
                <td className="px-3 py-2 text-right">
                  <div className="text-[var(--text-primary)]">{d.quote?.tcv != null ? formatCurrency(d.quote.tcv) : '—'}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{d.quote?.arr != null ? `${formatCurrency(d.quote.arr)}/yr` : ''}</div>
                </td>

                {/* Margin */}
                <td className={`px-3 py-2 text-right font-mono ${marginColour(d.quote?.gross_profit_margin)}`}>
                  {d.quote?.gross_profit_margin != null ? `${d.quote.gross_profit_margin.toFixed(1)}%` : '—'}
                </td>

                {/* Impl fee + scope-sync */}
                <td className="px-3 py-2 text-right">
                  <div className="text-[var(--text-primary)]">{d.quote?.implementation_fee != null ? formatCurrency(d.quote.implementation_fee) : '—'}</div>
                  {(d.quote?.implementation_fee ?? 0) > 0 && (
                    <div className={`text-[10px] inline-flex items-center gap-0.5 ${feeSynced ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {feeSynced ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                      {feeSynced ? 'scope-synced' : 'not synced'}
                    </div>
                  )}
                </td>

                {/* Submitted by */}
                <td className="px-3 py-2 text-[var(--text-secondary)] text-[12px]">{submittedBy}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
